import { ZomentumRoutes } from "@/Contracts";
import { AllRolePermissions, RolePermissionEnum } from "@/Contracts/Users";
import { EIcons } from "@atoms/ZIcon/interface";
import { useFeatureFlag } from "@/Hooks/useFeature";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";

/*
	{
		...
		permission: (AllRolePermission | null)
		Item is shown if the mentioned permission is enabled. By default null for items which do not require a permission.
	}
*/

export default () => {
	const isMarketplacePipelineSyncingEnabled = useFeatureFlag(
		"marketplace_pipeline_syncing"
	);
	const showPartnerAlignFeaturesFlag = useFeatureFlag(
		"show_partner_align_features"
	);
	const isMcpPermissionsEnabled = useFeatureFlag("mcp_permissions");
	const userCompany = useSelector(
		(state: GlobalState) => state.user.userCompany
	);
	// MCP agents act with the caller's own permissions, so the surface is admin-only; the session mint refuses a
	// non-admin anyway, and an entry that only ever errors is worse than no entry. Mirrors the backend's
	// User.isCompanyAdmin exactly - owner OR the User Management group - so an owner without that group still
	// sees the entry, matching the access the API grants them.
	const isAdmin = useSelector((state: GlobalState) =>
		Boolean(
			state.user.userData?.is_owner ||
				state.user.userData?.roles_permissions?.role ===
					RolePermissionEnum.ADMIN
		)
	);
	const settingsMenuItems = [
		{
			route: ZomentumRoutes.Preferences,
			icon: EIcons.SLIDERVERTICAL,
			title: "My Preferences",
			permission: null
		},
		{
			route: ZomentumRoutes.Notifications,
			icon: EIcons.NOTIFICATION,
			title: "Notifications",
			permission: null
		},
		{
			route: ZomentumRoutes.EmailTemplateSettings,
			icon: EIcons.EMAILTEMPLATE,
			title: "Email Templates",
			permission: AllRolePermissions.EMAIL_TEMPLATE_READ
		},
		{
			title: "Settings",
			disabled: true,
			permission: null
		},
		{
			route: ZomentumRoutes.AccountSettings,
			icon: EIcons.USERCIRCLE,
			title: "Account",
			permission: AllRolePermissions.ACCOUNT_READ
		},
		{
			route: ZomentumRoutes.EmailSettings,
			icon: EIcons.MAIL,
			title: "Email Settings",
			permission: AllRolePermissions.EMAIL_UPDATE
		},
		{
			route: ZomentumRoutes.UserSettings,
			icon: EIcons.USERSGROUP,
			title: "Users",
			permission: AllRolePermissions.USER_READ
		},
		{
			route: ZomentumRoutes.Suppliers,
			icon: EIcons.SUPPLIER,
			title: "Suppliers",
			permission: AllRolePermissions.SUPPLIER_READ
		},
		{
			route: ZomentumRoutes.PipelineSettings,
			icon: EIcons.PIPELINES,
			title: "Pipelines",
			permission: AllRolePermissions.PIPELINE_VIEW
		},
		{
			route: ZomentumRoutes.CustomFieldsSettings,
			icon: EIcons.CUSTOMEFIELDS,
			title: "Custom Fields",
			permission: AllRolePermissions.CUSTOM_FIELD_VIEW
		},
		{
			route: ZomentumRoutes.IntegrationSettings,
			icon: EIcons.INTEGRATIONS,
			title: "Integrations",
			permission: AllRolePermissions.COMPANY_CONNECT
		},
		{
			route: ZomentumRoutes.TaxRegionSettings,
			icon: EIcons.TAXSETUP,
			title: "Tax Setup",
			permission: AllRolePermissions.TAX_SETUP_READ
		}
	];
	if (!isMarketplacePipelineSyncingEnabled && showPartnerAlignFeaturesFlag) {
		settingsMenuItems.push({
			route: ZomentumRoutes.MarketplaceSettingsPage,
			icon: EIcons.MARKETPLACE,
			title: "Align Settings",
			permission: AllRolePermissions.ACCOUNT_READ
		});
	}
	const hasMcpAgentsAddon = userCompany?.mcp_agents_enabled ?? false;
	if (isMcpPermissionsEnabled && hasMcpAgentsAddon && isAdmin) {
		settingsMenuItems.push({
			route: ZomentumRoutes.McpAgents,
			icon: EIcons.AUDITLOGS,
			title: "MCP Agents",
			permission: AllRolePermissions.MCP_AUDIT_LOG_READ
		});
	}
	return settingsMenuItems;
};
